import { useCallback, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { chatApi, portalApi, getPortalToken, setPortalToken } from './api'
import PortalAccesses from './PortalAccesses'
import PortalChat from './PortalChat'
import PortalLogin from './PortalLogin'
import PortalOverview from './PortalOverview'
import PortalCodeOrders from './PortalCodeOrders'
import PortalTickets from './PortalTickets'
import PortalUsers from './PortalUsers'

/**
 * Кабинет клиента целиком.
 *
 * Живёт рядом с программой, но не внутри неё: свой токен, свой вход, свои
 * запросы. Общего с программой — только адрес сервера и оформление.
 */
type Page = 'overview' | 'chat' | 'requests' | 'orders' | 'codes' | 'accesses' | 'users'

export default function PortalApp() {
  const qc = useQueryClient()
  const [token, setTokenState] = useState<string | null>(getPortalToken)
  // Обзор первым: за десять секунд видно, до какого числа оплачено и не
  // забыли ли про вопросы. Остальное — по надобности.
  const [page, setPage] = useState<Page>('overview')
  // Ссылка из переписки: в какой раздел уйти и какую карточку раскрыть
  const [openTicketId, setOpenTicketId] = useState<number | null>(null)

  // Сессия кабинета истекла — возвращаем на вход, не трогая сессию программы
  useEffect(() => {
    const onOut = () => setTokenState(null)
    window.addEventListener('portal:unauthorized', onOut)
    return () => window.removeEventListener('portal:unauthorized', onOut)
  }, [])

  const entered = useCallback(() => {
    setTokenState(getPortalToken())
    qc.invalidateQueries({ queryKey: ['portal'] })
  }, [qc])

  const me = useQuery({
    queryKey: ['portal', 'me'],
    queryFn: portalApi.me,
    enabled: !!token,
    retry: false,
  })

  // Значок непрочитанного. Отдельным запросом: он нужен на каждой странице,
  // а тянуть ради него весь список бесед незачем.
  const unreadQuery = useQuery({
    queryKey: ['portal', 'unread'],
    queryFn: chatApi.unread,
    enabled: !!token,
    refetchInterval: 30_000,
  })
  const unread = unreadQuery.data?.unread ?? 0

  if (!token) return <PortalLogin onEntered={entered} />
  if (me.isLoading) return <div className="empty">Загрузка…</div>
  if (me.isError || !me.data) return <PortalLogin onEntered={entered} />

  const account = me.data

  return (
    <div className="app">
      <nav className="sidebar">
        <div className="brand">
          <img src="/brand/logo-mark-white.png" alt="Бизнес Эра" className="brand-logo" />
          <span className="brand-app">Личный кабинет</span>
        </div>
        <div className="who" style={{ marginTop: 8, marginBottom: 12 }}>
          {account.client_name}
        </div>
        <button
          className={`portal-nav${page === 'overview' ? ' on' : ''}`}
          onClick={() => setPage('overview')}
        >
          Обзор
        </button>
        <button
          className={`portal-nav${page === 'chat' ? ' on' : ''}`}
          onClick={() => setPage('chat')}
          title="Переписка с вашим менеджером и по обращениям"
        >
          Сообщения
          {unread > 0 && <span className="ms-badge">{unread}</span>}
        </button>
        <button
          className={`portal-nav${page === 'requests' ? ' on' : ''}`}
          onClick={() => setPage('requests')}
          title="Сопровождение и аренда 1С — по вашему тарифу"
        >
          Обращения
        </button>
        <button
          className={`portal-nav${page === 'orders' ? ' on' : ''}`}
          onClick={() => setPage('orders')}
          title="Коды маркировки и разовые работы"
        >
          Заявки
        </button>
        {/* Раздел особый: появляется, только если клиенту включили
            расширенный учёт кодов. Остальные о нём и не знают. */}
        {account.features?.includes('codes_tree') && (
          <button
            className={`portal-nav${page === 'codes' ? ' on' : ''}`}
            onClick={() => setPage('codes')}
            title="Поставки и коды маркировки по ним"
          >
            Заказы кодов
          </button>
        )}
        <button
          className={`portal-nav${page === 'accesses' ? ' on' : ''}`}
          onClick={() => setPage('accesses')}
        >
          Доступы
        </button>
        <button
          className={`portal-nav${page === 'users' ? ' on' : ''}`}
          onClick={() => setPage('users')}
        >
          Сотрудники
        </button>
        <div className="spacer" />
        <div className="who">
          {account.full_name}
          <br />
          {account.is_admin ? 'Ответственный за кабинет' : (account.position ?? 'Сотрудник')}
        </div>
        <button
          className="btn logout"
          onClick={() => {
            setPortalToken(null)
            setTokenState(null)
            qc.clear()
          }}
        >
          Выйти
        </button>
      </nav>

      <main className="main">
        {page === 'chat' ? (
          <PortalChat
            me={account}
            onOpenTicket={(id, kind) => {
              setOpenTicketId(id)
              setPage(kind === 'order' ? 'orders' : 'requests')
            }}
          />
        ) : page === 'overview' ? (
          <PortalOverview me={account} onOpenTickets={() => setPage('requests')} />
        ) : page === 'requests' ? (
          <PortalTickets
            me={account}
            kind="request"
            initialOpenId={openTicketId}
            onOpened={() => setOpenTicketId(null)}
          />
        ) : page === 'codes' ? (
          <PortalCodeOrders me={account} />
        ) : page === 'orders' ? (
          <PortalTickets
            me={account}
            kind="order"
            initialOpenId={openTicketId}
            onOpened={() => setOpenTicketId(null)}
          />
        ) : page === 'accesses' ? (
          <PortalAccesses me={account} />
        ) : (
          <PortalUsers me={account} />
        )}
      </main>
    </div>
  )
}

/**
 * Переход по ссылке из письма.
 *
 * Отдельного подтверждения почты не просим: письмо дошло до этого адреса,
 * значит адрес его. Ссылка одноразовая — открыть её дважды нельзя.
 */
/* Ссылка одноразовая, и запрос по ней должен уйти ровно один раз. В режиме
   разработки React намеренно вызывает эффекты дважды, да и двойной щелчок
   никто не отменял — запоминаем запрос по самой ссылке и переиспользуем. */
const acceptInFlight = new Map<string, Promise<{ access_token: string | null }>>()

function acceptOnce(token: string) {
  let started = acceptInFlight.get(token)
  if (!started) {
    started = portalApi.acceptInvite(token)
    acceptInFlight.set(token, started)
  }
  return started
}

export function PortalInvite({ token }: { token: string }) {
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) return
    let alive = true
    acceptOnce(token)
      .then((res) => {
        if (!alive) return
        if (res.access_token) {
          setPortalToken(res.access_token)
          setDone(true)
          // Ссылку из адреса убираем сразу: она одноразовая, и оставлять её
          // в истории браузера и в заголовке вкладки незачем.
          window.location.replace(`${window.location.pathname}#/portal`)
        } else {
          setError('Ссылка не сработала')
        }
      })
      .catch((e: any) => alive && setError(e?.message ?? 'Ссылка недействительна'))
    return () => {
      alive = false
    }
  }, [token])

  return (
    <div className="login-page">
      <div className="login-box">
        <img src="/brand/logo-full.png" alt="Бизнес Эра" className="login-logo" />
        <h1>Личный кабинет</h1>
        {error ? (
          <>
            <div className="error">{error}</div>
            <p className="hint">
              Попросите ответственного за кабинет в вашей организации прислать
              приглашение заново.
            </p>
            <a className="btn" style={{ width: '100%' }} href="#/portal">
              К входу
            </a>
          </>
        ) : (
          <p className="hint">{done ? 'Входим…' : 'Проверяем ссылку…'}</p>
        )}
      </div>
    </div>
  )
}
